/**
 * Skeleton Components — Soorgaa Design System
 * 
 * Provides accessible, zero-layout-shift loading placeholders.
 * Uses animate-pulse which automatically complies with prefers-reduced-motion.
 */
import React from "react";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

export function Skeleton({ className = "", ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={`bg-muted/70 rounded-md animate-pulse ${className}`.trim()}
      {...props}
    />
  );
}

export function SkeletonCard({ className = "", lines: _lines }: { className?: string; lines?: number }) {
  return (
    <div
      aria-hidden="true"
      className={`p-5 rounded-lg border border-border bg-card shadow-xs space-y-3.5 ${className}`.trim()}
    >
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-4 w-12 rounded-xs" />
      </div>
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-2/3" />
      <div className="pt-3 border-t border-border/50 flex items-center justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-24 rounded-md" />
      </div>
    </div>
  );
}

export function SkeletonList({ count = 4, className = "" }: { count?: number; className?: string }) {
  return (
    <div aria-hidden="true" className={`space-y-3 ${className}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between p-3.5 bg-card border border-border rounded-lg"
        >
          <div className="flex items-center gap-3 w-3/4">
            <Skeleton className="w-9 h-9 rounded-md shrink-0" />
            <div className="space-y-1.5 w-full">
              <Skeleton className="h-4 w-3/5" />
              <Skeleton className="h-3 w-2/5" />
            </div>
          </div>
          <Skeleton className="h-8 w-16 rounded-md" />
        </div>
      ))}
    </div>
  );
}
